import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const SIGNS = [
  "Difficulty sounding out words",
  "Avoiding reading tasks",
  "Trouble remembering sight words",
  "Slow or choppy reading",
  "Difficulty spelling common words",
  "Frustration or low confidence around reading",
  "Trouble rhyming or identifying sounds in words",
];

export default function Support() {
  return (
    <section
      id="support"
      data-testid="support-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
            <span className="w-1.5 h-1.5 rounded-full bg-wg-magenta" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">Dyslexia Support</span>
          </div>
          <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
            Understanding <span className="text-wg-purple">Dyslexia</span> Support
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-wg-ink2">
            Dyslexia can affect reading, spelling, decoding, fluency, and written language skills,
            but with the right instruction and encouragement, children can make meaningful progress.
            Word Gumbo Learning Co. provides personalized support to help families understand their
            child's learning needs and build a path forward.
          </p>

          <div className="mt-8 rounded-3xl bg-gradient-to-br from-wg-purple to-wg-purpleDeep text-white p-7 md:p-8 shadow-lg shadow-wg-purple/20">
            <h3 className="font-heading font-bold text-xl md:text-2xl">Not sure where to start?</h3>
            <p className="mt-2 text-white/85">
              A free consultation gives families the chance to ask questions, share concerns, and
              learn which service may be the best fit for their child.
            </p>
            <a
              href="#consultation"
              data-testid="support-cta-btn"
              className="mt-5 inline-flex items-center gap-2 bg-wg-yellow text-wg-ink rounded-full px-6 py-3 font-semibold hover:-translate-y-0.5 transition-transform"
            >
              Schedule a Free Consultation
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="lg:col-span-7"
        >
          <div className="bg-white rounded-[2rem] border border-wg-purple/10 shadow-[0_18px_50px_-20px_rgba(74,29,82,0.15)] p-8 md:p-10">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-wg-ink">
              Signs your child may need reading support
            </h3>
            <p className="mt-2 text-wg-ink2">
              If any of these sound familiar, an evaluation or consultation may bring helpful clarity.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3" data-testid="signs-list">
              {SIGNS.map((sign, i) => (
                <motion.li
                  key={sign}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex items-start gap-3 rounded-2xl p-4 bg-wg-cream2/70 border border-wg-purple/5"
                >
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-wg-teal text-white shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <span className="text-[15px] text-wg-ink2 leading-relaxed">{sign}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
